import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight, faXmark, faCoins, faEllipsisVertical } from "@fortawesome/free-solid-svg-icons";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import styles from "../pages/Dashboard.module.css";

// ── Mobile: bottom sheet ───────────────────────────────────────────────────────
function ActionMenuMobile({ label, open, setOpen, visibleOptions, handleSelect, hideTrigger, iconOnly }) {
  // Sem isso a pagina por tras continua rolando (iOS nao contem scroll so
  // com overflow:hidden), o que combinado com a sheet sem altura maxima
  // (ver .sheet no CSS) fazia os primeiros itens ficarem empurrados pra
  // fora da tela, acima do topo — ver auditoria mobile-first, achado C-novo.
  useBodyScrollLock(open);

  return (
    <>
      {/* O header mobile do Dashboard ja tem o proprio botao "hamburguer" e e
          quem controla a abertura — nesse caso o gatilho interno e suprimido
          pra nao aparecerem dois. */}
      {!hideTrigger && (iconOnly ? (
        // Versão compacta (só "⋮") pra cabeçalhos estreitos no celular, como o
        // card de desafio: com o texto, o botão saía cortado pra fora do card.
        <button
          type="button"
          className={styles.actionMenuIconTrigger}
          onClick={() => setOpen(true)}
          aria-label={label}
          aria-haspopup="dialog"
        >
          <FontAwesomeIcon icon={faEllipsisVertical} aria-hidden="true" />
        </button>
      ) : (
      <button
        type="button"
        className={styles.actionMenuTrigger}
        onClick={() => setOpen(true)}
      >
        <span className={styles.actionMenuTriggerInner}>
          {label}
          <FontAwesomeIcon icon={faChevronDown} className={styles.actionMenuChevron} />
        </span>
      </button>
      ))}

      {open && (
        <div
          className={styles.sheetOverlay}
          onClick={() => setOpen(false)}
          role="presentation"
        >
          <div
            className={styles.sheet}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label={label}
          >
            <div className={styles.sheetHandle} />

            <div className={styles.sheetList}>
              {visibleOptions.map((opt) => (
                opt.section ? (
                  // Rotulo de grupo: nao e item de menu, entao role="presentation"
                  // pra nao entrar na contagem de itens do leitor de tela.
                  <div key={opt.key} className={styles.sheetSection} role="presentation">
                    {opt.label}
                  </div>
                ) : (
                <button
                  key={opt.key}
                  type="button"
                  className={`${styles.sheetItem} ${opt.variant === "danger" ? styles.sheetItemDanger : ""}`}
                  onClick={() => handleSelect(opt)}
                  disabled={opt.disabled}
                >
                  <span className={styles.sheetItemLeft}>
                    <span className={styles.sheetItemTitle}>
                      {opt.icon && (
                        <FontAwesomeIcon icon={opt.icon} className={styles.sheetItemIcon} />
                      )}
                      {opt.label}
                    </span>
                    {opt.sub && <span className={styles.sheetItemSub}>{opt.sub}</span>}
                  </span>
                  <span className={styles.sheetItemRightGroup}>
                    {opt.plus && (
                      <FontAwesomeIcon icon={faCoins} className={styles.sheetItemPlus} title="Recurso Plus" />
                    )}
                    <FontAwesomeIcon icon={faChevronRight} className={styles.sheetItemRight} />
                  </span>
                </button>
                )
              ))}
            </div>

            <button type="button" className={styles.sheetClose} onClick={() => setOpen(false)}>
              <FontAwesomeIcon icon={faXmark} />
              Fechar
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ── Desktop: popover portal ────────────────────────────────────────────────────
function ActionMenuDesktop({ label, open, setOpen, visibleOptions, handleSelect, computePosition, btnRef, pos }) {
  useEffect(() => {
    if (!open) return;
    const onKey    = (e) => e.key === "Escape" && setOpen(false);
    const onResize = () => computePosition();
    const onScroll = () => computePosition();
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, setOpen, computePosition]);

  const openDesktop = () => {
    computePosition();
    setOpen(true);
  };

  return (
    <>
      <div className={styles.actionMenuWrap}>
        <button
          ref={btnRef}
          type="button"
          className={styles.actionMenuTrigger}
          onClick={() => (open ? setOpen(false) : openDesktop())}
          aria-expanded={open}
        >
          <span className={styles.actionMenuTriggerInner}>
            {label}
            <FontAwesomeIcon icon={faChevronDown} className={styles.actionMenuChevron} />
          </span>
        </button>
      </div>

      {open &&
        createPortal(
          <>
            <div
              className={styles.menuPortalBackdrop}
              onClick={() => setOpen(false)}
              role="presentation"
            />
            <div
              className={styles.menuPortalPopover}
              style={{ top: pos.top, left: pos.left, width: pos.width }}
              role="menu"
              aria-label={label}
            >
              {visibleOptions.map((opt, idx) => (
                opt.section ? (
                  <div key={opt.key} className={styles.menuPortalSection} role="presentation">
                    {opt.label}
                  </div>
                ) : (
                <button
                  key={opt.key}
                  type="button"
                  className={`${styles.menuPortalItem} ${opt.variant === "danger" ? styles.menuPortalItemDanger : ""}`}
                  onClick={() => handleSelect(opt)}
                  disabled={opt.disabled}
                  role="menuitem"
                  style={{
                    borderBottom:
                      idx === visibleOptions.length - 1
                        ? "none"
                        : "1px solid var(--border-0-06)",
                  }}
                >
                  <span className={styles.menuPortalItemLeft}>
                    <span className={styles.menuPortalItemTitle}>
                      {opt.icon && (
                        <FontAwesomeIcon icon={opt.icon} className={styles.menuPortalItemIcon} />
                      )}
                      {opt.label}
                    </span>
                    {opt.sub && (
                      <span className={styles.menuPortalItemSub}>{opt.sub}</span>
                    )}
                  </span>
                  <span className={styles.menuPortalItemRightGroup}>
                    {opt.plus && (
                      <FontAwesomeIcon icon={faCoins} className={styles.menuPortalItemPlus} title="Recurso Plus" />
                    )}
                    <FontAwesomeIcon icon={faChevronRight} className={styles.menuPortalItemRight} />
                  </span>
                </button>
                )
              ))}
            </div>
          </>,
          document.body
        )}
    </>
  );
}

// ── Public component ───────────────────────────────────────────────────────────
/**
 * Menu de ações responsivo: bottom sheet em mobile, popover em desktop.
 *
 * @param {boolean}  isMobile
 * @param {Array}    options   - [{ key, icon, label, sub, onClick, hidden, disabled, variant, plus }]
 *   Um item com `section: true` vira um RÓTULO de grupo (não clicável), usado
 *   pra separar "Alunos" / "Conteúdo" / "Financeiro" / "Conta" num menu que
 *   hoje é uma lista plana. Cabeçalho sem nenhum item visível abaixo é
 *   descartado automaticamente — rótulo órfão confunde mais do que a lista
 *   plana que ele deveria organizar.
 *   plus: true mostra um ícone de moedas à direita, indicando que é um
 *   recurso liberado pelo plano Plus (ver PaymentsModal.jsx PLUS_BENEFITS).
 * @param {string}   label     - texto do botão trigger
 * @param {boolean}  [open]        - se informado, o componente vira CONTROLADO
 *   e quem manda na abertura e o pai (usado pelo header mobile do Dashboard,
 *   que ja tem o proprio botao de menu).
 * @param {Function} [onOpenChange] - obrigatorio no modo controlado
 * @param {boolean}  [hideTrigger]  - nao renderiza o gatilho interno
 */
export default function ActionMenu({
  isMobile,
  options = [],
  label = "Ações",
  open: openProp,
  onOpenChange,
  hideTrigger = false,
  iconOnly = false, // só no celular: gatilho "⋮" em vez de "Ações ▾"
}) {
  const [openInterno, setOpenInterno] = useState(false);
  const controlado = openProp !== undefined;
  const open = controlado ? openProp : openInterno;
  const setOpen = useCallback((valor) => {
    const proximo = typeof valor === "function" ? valor(open) : valor;
    if (controlado) onOpenChange?.(proximo);
    else setOpenInterno(proximo);
  }, [controlado, onOpenChange, open]);
  const [pos, setPos]     = useState({ top: 0, left: 0, width: 280 });
  const btnRef            = useRef(null);
  const visibleOptions    = useMemo(() => {
    const visiveis = options.filter((o) => !o.hidden);
    // Mantem um cabecalho apenas se existir item real logo abaixo dele, antes
    // do proximo cabecalho. Cobre os dois casos ruins: grupo inteiro oculto
    // por papel/plano, e cabecalhos consecutivos.
    return visiveis.filter((o, i) => {
      if (!o.section) return true;
      const proximo = visiveis[i + 1];
      return Boolean(proximo) && !proximo.section;
    });
  }, [options]);

  const handleSelect = async (opt) => {
    if (opt.disabled) return;
    setOpen(false);
    await opt.onClick?.();
  };

  const computePosition = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r     = el.getBoundingClientRect();
    const width = Math.max(280, r.width);
    const left  = Math.min(window.innerWidth - width - 12, r.right - width);
    setPos({ top: r.bottom + 10, left: Math.max(12, left), width });
  }, []);

  // Fecha ao alternar desktop<->mobile (o menu aberto de um layout nao faz
  // sentido no outro). Pula a primeira execucao: no modo controlado, disparar
  // onOpenChange(false) na montagem mexeria no estado do pai sem necessidade.
  const primeiraRenderizacao = useRef(true);
  useEffect(() => {
    if (primeiraRenderizacao.current) { primeiraRenderizacao.current = false; return; }
    setOpen(false);
    // setOpen fora das deps de proposito: e recriado a cada mudanca de `open`
    // e re-executaria este efeito, fechando o menu no ato de abrir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  if (visibleOptions.length === 0) return null;

  if (isMobile) {
    return (
      <ActionMenuMobile
        label={label}
        open={open}
        setOpen={setOpen}
        visibleOptions={visibleOptions}
        handleSelect={handleSelect}
        hideTrigger={hideTrigger}
        iconOnly={iconOnly}
      />
    );
  }

  return (
    <ActionMenuDesktop
      label={label}
      open={open}
      setOpen={setOpen}
      visibleOptions={visibleOptions}
      handleSelect={handleSelect}
      computePosition={computePosition}
      btnRef={btnRef}
      pos={pos}
    />
  );
}
